// TimeKalendar — osobní kalendář + statistika měřicích rolí (6.8.2026)
// ---------------------------------------------------------------------------
// Data: GET /api/time-report?kalendar=1[&tyden=YYYY-MM-DD | &mesic=YYYY-MM]
// Pravidla se nemění — jen je konečně vidět, co člověk naměřil:
//   • týdenní mřížka s bloky (mobil = karty dnů pod sebou)
//   • měsíční přehled dnů s hodinami a stavem reportu
//   • klik na blok → Upravit záznam (do 2 h od konce, konec jen dozadu max 60 min)
//   • statistika: podle činnosti, kvadrantu, úkolu a původu záznamu
import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, BarChart3, Clock, Pencil, Check, AlertTriangle, Sun, Loader2, ListChecks, X } from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import { toast } from "../../components/Toast.jsx";
import EditEntryModal from "../../components/EditEntryModal.jsx";
import {
  ACTIVITY_COLORS, QX_DESC, QX_SOLID, PUVOD_META, DAY_NAMES, MONTH_NAMES,
  activitySolid, activityBlock, fmtHm, fmtDayCz, fmtDayFull, dowIdx, addDaysIso,
  mondayOf, minutesOf,
} from "../../lib/timeMeta.js";

const PX_PER_HOUR = 46;

const pragueToday = () => {
  try { return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Prague" }).format(new Date()); }
  catch { return new Date().toISOString().slice(0, 10); }
};

/* ─── proužkový graf ──────────────────────────────────────────────────── */
function Bars({ items, total, empty = "Zatím nic naměřeno." }) {
  if (!items.length) return <div className="text-sm text-slate-400 py-2">{empty}</div>;
  const max = Math.max(...items.map((i) => i.sec), 1);
  return (
    <div className="space-y-2.5">
      {items.map((i) => (
        <div key={i.key}>
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-sm font-semibold text-slate-700 truncate min-w-0">{i.label}</span>
            <span className="text-xs font-bold tabular-nums text-slate-500 shrink-0">
              {fmtHm(i.sec)}
              {total > 0 && <span className="ml-1.5 font-semibold text-slate-400">{Math.round((i.sec / total) * 100)} %</span>}
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full ${i.color}`} style={{ width: `${Math.max(3, (i.sec / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Kpi({ label, value, sub, tone = "slate" }) {
  const tones = {
    slate: "bg-white border-slate-200", brand: "bg-brand-50 border-brand-200",
    emerald: "bg-emerald-50 border-emerald-200", amber: "bg-amber-50 border-amber-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-xl sm:text-2xl font-extrabold tabular-nums text-slate-900 mt-0.5 [overflow-wrap:anywhere]">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ─── jeden blok v seznamu dne ────────────────────────────────────────── */
function BlokRadek({ e, useQuadrants, onEdit }) {
  const pv = PUVOD_META[e.puvod] || PUVOD_META.live;
  return (
    <div className={`flex items-center gap-2 flex-wrap py-2 ${e.invalid ? "opacity-60" : ""}`}>
      <span className="text-xs text-slate-400 tabular-nums shrink-0 w-20">{e.od || "?"}{e.do ? `-${e.do}` : ""}</span>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${ACTIVITY_COLORS[e.oblast] || "bg-slate-100 text-slate-700"}`}>{e.oblast}</span>
      {useQuadrants && e.qx && <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 shrink-0" title={QX_DESC[e.qx]}>{e.qx}</span>}
      <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${pv.cls}`} title={pv.title}>{pv.label}</span>
      {e.ukol_nazev && (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5 shrink-0 max-w-[180px]">
          <ListChecks className="w-3 h-3 shrink-0" /> <span className="truncate">{e.ukol_nazev}</span>
        </span>
      )}
      {e.invalid && (
        <span className="text-[11px] font-bold px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 shrink-0" title={e.invalid_note || ""}>Zneplatněno</span>
      )}
      {e.popis ? <span className="text-sm text-slate-700 flex-1 truncate min-w-[80px]">{e.popis}</span> : <span className="flex-1" />}
      <span className="text-sm font-semibold tabular-nums text-slate-800 shrink-0">{fmtHm(e.sec)}</span>
      {(e.editable || e.zkratitelne) && onEdit && (
        <button onClick={() => onEdit(e)} title="Upravit záznam"
          className="shrink-0 p-1.5 -m-0.5 rounded-xl text-slate-400 hover:text-brand-600 hover:bg-brand-50">
          <Pencil className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/* ─── detail dne ──────────────────────────────────────────────────────── */
function DenDetail({ den, useQuadrants, bulletsRequired, onEdit, onClose }) {
  if (!den) return null;
  return (
    <div className="bg-white border border-brand-200 ring-2 ring-brand-100 rounded-xl shadow-sm p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between [&>*]:min-w-0 gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {DAY_NAMES[dowIdx(den.datum)]}{den.today ? <><span aria-hidden className="inline-block align-middle w-px h-3 bg-current opacity-40 mx-1.5" />dnes</> : null}
          </div>
          <div className="text-lg font-bold text-slate-900">{fmtDayFull(den.datum)}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm font-extrabold tabular-nums text-slate-800">{fmtHm(den.sec)}</span>
          <button onClick={onClose} className="btn-ikona"><X className="w-4 h-4" /></button>
        </div>
      </div>
      {den.volno && (
        <div className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-xl px-2 py-1 mb-2">
          <Sun className="w-3.5 h-3.5" /> Volno{den.volnoHodiny ? ` ${den.volnoHodiny}` : ""}
        </div>
      )}
      {den.entries.length === 0 ? (
        <div className="text-sm text-slate-400 py-2">Žádný záznam.</div>
      ) : (
        <div className="divide-y divide-slate-50">
          {den.entries.map((e) => <BlokRadek key={e.id} e={e} useQuadrants={useQuadrants} onEdit={onEdit} />)}
        </div>
      )}
      {bulletsRequired && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Denní report</div>
          {den.bulletsText
            ? <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans [overflow-wrap:anywhere]">{den.bulletsText}</pre>
            : <div className={`text-sm ${den.required ? "text-amber-600 font-semibold" : "text-slate-400"}`}>
                {den.required ? (den.editableReport ? "Chybí, ještě jde doplnit na záložce Dnes." : "Nevyplněno (den je zamčený).") : "—"}
              </div>}
        </div>
      )}
    </div>
  );
}

/* ─── hlavní komponenta ───────────────────────────────────────────────── */
export default function TimeKalendar({ mode = "kalendar", roleHint = "", asEmail = "", readOnly = false }) {
  const todayIso = useMemo(() => pragueToday(), []);
  const [view, setView] = useState("tyden");            // tyden | mesic
  const [weekMon, setWeekMon] = useState(() => mondayOf(todayIso));
  const [month, setMonth] = useState(() => todayIso.slice(0, 7));
  const [selDatum, setSelDatum] = useState("");
  const [editEntry, setEditEntry] = useState(null);
  const [busy, setBusy] = useState(false);

  const extra = `${roleHint ? `&roleHint=${encodeURIComponent(roleHint)}` : ""}${asEmail ? `&as=${encodeURIComponent(asEmail)}` : ""}`;
  const endpoint = `/api/time-report?kalendar=1&${view === "tyden" ? `tyden=${weekMon}` : `mesic=${month}`}${extra}`;
  const { data, loading, refetch } = useApiData(endpoint);

  useEffect(() => {
    const onChanged = () => refetch?.();
    window.addEventListener("fw40-time-changed", onChanged);
    return () => window.removeEventListener("fw40-time-changed", onChanged);
  }, [refetch]);

  const days = data?.days || [];
  const totals = data?.totals || {};
  const useQuadrants = !!data?.useQuadrants;
  const bulletsRequired = !!data?.bulletsRequired;
  const denMap = useMemo(() => new Map(days.map((d) => [d.datum, d])), [days]);
  const selDen = selDatum ? denMap.get(selDatum) : null;

  // Když se přepne období, zavři detail dne, který v něm není.
  useEffect(() => { if (selDatum && !denMap.has(selDatum)) setSelDatum(""); }, [denMap, selDatum]);

  const post = async (payload) => {
    if (readOnly) return false;
    setBusy(true);
    try {
      const body = roleHint ? { ...payload, roleHint } : payload;
      const r = await apiFetch("/api/time-report", { method: "POST", body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Akce selhala");
      await refetch();
      window.dispatchEvent(new Event("fw40-time-changed"));
      return true;
    } catch (e) { toast("Chyba: " + (e?.message || e), "error"); return false; }
    finally { setBusy(false); }
  };

  /* ── navigace obdobím ── */
  const curWeek = mondayOf(todayIso);
  const curMonth = todayIso.slice(0, 7);
  const shiftMonth = (m, n) => {
    const y = Number(m.slice(0, 4)), mm = Number(m.slice(5, 7)) - 1 + n;
    const d = new Date(y, mm, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const atNewest = view === "tyden" ? weekMon >= curWeek : month >= curMonth;
  const goPrev = () => (view === "tyden" ? setWeekMon((w) => addDaysIso(w, -7)) : setMonth((m) => shiftMonth(m, -1)));
  const goNext = () => {
    if (atNewest) return;
    if (view === "tyden") setWeekMon((w) => (addDaysIso(w, 7) > curWeek ? curWeek : addDaysIso(w, 7)));
    else setMonth((m) => (shiftMonth(m, 1) > curMonth ? curMonth : shiftMonth(m, 1)));
  };
  const goToday = () => (view === "tyden" ? setWeekMon(curWeek) : setMonth(curMonth));
  const obdobiLabel = view === "tyden"
    ? `${fmtDayCz(weekMon)} – ${fmtDayFull(addDaysIso(weekMon, 6))}`
    : `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

  /* ── rozsah hodin pro mřížku ── */
  const bounds = useMemo(() => {
    let min = 24 * 60, max = 0;
    for (const d of days) for (const e of d.entries) {
      const s = minutesOf(e.od), en = minutesOf(e.do);
      if (s != null) min = Math.min(min, s);
      if (en != null) max = Math.max(max, en);
    }
    if (max <= min) return { fromH: 8, toH: 18 };
    return { fromH: Math.max(0, Math.floor(min / 60)), toH: Math.min(24, Math.max(Math.ceil(max / 60), Math.floor(min / 60) + 1)) };
  }, [days]);
  const hodiny = useMemo(() => {
    const out = [];
    for (let h = bounds.fromH; h < bounds.toH; h++) out.push(h);
    return out;
  }, [bounds]);
  const gridH = hodiny.length * PX_PER_HOUR;

  // Jednoduché „lajny" pro překrývající se bloky, ať se nepřekreslují přes sebe.
  const layout = (entries) => {
    const list = entries
      .map((e) => ({ e, s: minutesOf(e.od), en: minutesOf(e.do) }))
      .filter((x) => x.s != null && x.en != null && x.en > x.s)
      .sort((a, b) => a.s - b.s);
    const lanes = [];
    for (const it of list) {
      let i = lanes.findIndex((konec) => konec <= it.s);
      if (i < 0) { lanes.push(it.en); i = lanes.length - 1; } else lanes[i] = it.en;
      it.lane = i;
    }
    return { list, lanes: Math.max(1, lanes.length) };
  };

  const openEdit = (e) => { if (!readOnly && (e.editable || e.zkratitelne)) setEditEntry(e); };

  /* ── statistika ── */
  const statActivity = Object.entries(totals.byActivity || {}).sort((a, b) => b[1] - a[1])
    .map(([k, sec]) => ({ key: k, label: k, sec, color: activitySolid(k) }));
  const statQx = ["Q1", "Q2", "Q3", "Q4"].filter((x) => (totals.byQuadrant || {})[x])
    .map((x) => ({ key: x, label: `${x}: ${QX_DESC[x]}`, sec: totals.byQuadrant[x], color: QX_SOLID[x] }));
  const statUkol = Object.entries(totals.byUkol || {}).sort((a, b) => b[1] - a[1]).slice(0, 8)
    .map(([k, sec]) => ({ key: k, label: k, sec, color: "bg-brand-500" }));
  const statPuvod = Object.entries(totals.origins || {}).filter(([, s]) => s > 0)
    .map(([k, sec]) => ({ key: k, label: (PUVOD_META[k] || { label: k }).label, sec, color: k === "live" ? "bg-emerald-500" : k === "zpetny_start" ? "bg-amber-500" : "bg-rose-500" }));

  return (
    <div className="space-y-4">
      {/* hlavička období */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between [&>*]:flex-wrap [&>*]:min-w-0 gap-3">
          <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
            <button onClick={goPrev} title="Předchozí" className="btn-ikona shrink-0">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={goNext} disabled={atNewest} title="Následující"
              className="btn-ikona shrink-0 disabled:opacity-40">
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="text-base sm:text-lg font-bold text-slate-900 px-1 min-w-0">{obdobiLabel}</div>
            {!atNewest && (
              <button onClick={goToday} className="btn btn-secondary btn-sm shrink-0">
                {view === "tyden" ? "Tento týden" : "Tento měsíc"}
              </button>
            )}
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-300 shrink-0" />}
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
            <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden shrink-0">
              {[["tyden", "Týden"], ["mesic", "Měsíc"]].map(([v, l]) => (
                <button key={v} onClick={() => setView(v)}
                  className={`text-xs font-bold px-3 py-2 transition ${view === v ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
            <div className="text-right shrink-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Celkem za {view === "tyden" ? "týden" : "měsíc"}</div>
              <div className="text-xl sm:text-2xl font-extrabold tabular-nums text-slate-900 leading-tight">{fmtHm(totals.sec || 0)}</div>
            </div>
          </div>
        </div>
        {/* barevný pruh rozdělení činností */}
        {(totals.sec || 0) > 0 && (
          <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-slate-100">
            {statActivity.map((a) => (
              <div key={a.key} className={a.color} style={{ width: `${(a.sec / totals.sec) * 100}%` }} title={`${a.label}: ${fmtHm(a.sec)}`} />
            ))}
          </div>
        )}
      </div>

      {mode === "statistika" ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            <Kpi label="Naměřeno" value={fmtHm(totals.sec || 0)} tone="brand" sub={`${totals.bloku || 0} bloků`} />
            <Kpi label="Průměr na den" value={fmtHm(totals.prumerSec || 0)} sub={`${totals.dnuSMerenim || 0} dnů s měřením`} />
            <Kpi label="Nejvíc času" value={statActivity[0]?.label || "—"} sub={statActivity[0] ? fmtHm(statActivity[0].sec) : ""} />
            {bulletsRequired
              ? <Kpi label="Reporty" value={`${totals.reportuHotovo || 0}/${totals.reportuPovinnych || 0}`} tone={(totals.reportuHotovo || 0) >= (totals.reportuPovinnych || 0) ? "emerald" : "amber"} sub="odevzdaných denních reportů" />
              : <Kpi label="Měřeno živě" value={`${totals.sec ? Math.round(((totals.origins?.live || 0) / totals.sec) * 100) : 0} %`} sub="zbytek doplněn zpětně" />}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-5 h-5 text-brand-600" /><h3 className="text-base font-bold text-slate-900">Podle činnosti</h3></div>
              <Bars items={statActivity} total={totals.sec || 0} />
            </div>
            {useQuadrants && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-5 h-5 text-rose-500" /><h3 className="text-base font-bold text-slate-900">Podle kvadrantu</h3></div>
                <Bars items={statQx} total={totals.sec || 0} empty="Bloky zatím nemají kvadrant." />
                <div className="text-[11px] text-slate-400 mt-3 leading-snug">Cíl je co nejvíc Q2 (důležité, neurgentní), to je práce, která posouvá, ne hašení požárů.</div>
              </div>
            )}
            {statUkol.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3"><ListChecks className="w-5 h-5 text-brand-600" /><h3 className="text-base font-bold text-slate-900">Podle úkolu (top 8)</h3></div>
                <Bars items={statUkol} total={totals.sec || 0} />
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-slate-500" /><h3 className="text-base font-bold text-slate-900">Jak vznikly záznamy</h3></div>
              <Bars items={statPuvod} total={totals.sec || 0} />
              <div className="text-[11px] text-slate-400 mt-3 leading-snug">Čím víc „Živě", tím přesnější čísla. Zpětně jde doplnit maximálně 2 hodiny.</div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* ── TÝDEN ── */}
          {view === "tyden" && (
            <>
              {/* mobil: karty dnů */}
              <div className="sm:hidden space-y-2">
                {days.map((d) => {
                  const aktivni = selDatum === d.datum;
                  return (
                    <button key={d.datum} onClick={() => setSelDatum(aktivni ? "" : d.datum)}
                      className={`w-full text-left rounded-xl border p-3 transition ${
                        aktivni ? "border-brand-300 ring-2 ring-brand-100 bg-white"
                        : d.today ? "border-brand-200 bg-brand-50/40"
                        : d.volno ? "border-sky-200 bg-sky-50/60"
                        : "border-slate-200 bg-white"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold shrink-0 ${d.today ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                            {Number(d.datum.slice(8, 10))}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-bold text-slate-800">{DAY_NAMES[dowIdx(d.datum)]}</div>
                            <div className="text-[11px] text-slate-400">{fmtDayCz(d.datum)}{d.volno ? <><span aria-hidden className="inline-block align-middle w-px h-3 bg-current opacity-40 mx-1" />volno</> : null}</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-extrabold tabular-nums text-slate-900">{d.sec ? fmtHm(d.sec) : "—"}</div>
                          {bulletsRequired && !d.future && (
                            <div className={`text-[11px] font-bold ${d.filled ? "text-emerald-600" : d.required ? "text-amber-600" : "text-slate-300"}`}>
                              {d.filled ? "✓ report" : d.required ? "report chybí" : ""}
                            </div>
                          )}
                        </div>
                      </div>
                      {d.entries.length > 0 && (
                        <div className="flex gap-1 mt-2 h-1.5 rounded-full overflow-hidden bg-slate-100">
                          {d.entries.filter((e) => !e.invalid).map((e) => (
                            <div key={e.id} className={`${activitySolid(e.oblast)} rounded-full`} style={{ width: `${Math.max(4, (e.sec / Math.max(d.sec, 1)) * 100)}%` }} />
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* desktop: hodinová mřížka */}
              <div className="hidden sm:block bg-white border border-slate-200 rounded-xl shadow-sm p-3 overflow-x-auto">
                <div className="min-w-[680px]">
                  <div className="grid" style={{ gridTemplateColumns: `48px repeat(7, minmax(0, 1fr))` }}>
                    <div />
                    {days.map((d) => (
                      <button key={d.datum} onClick={() => setSelDatum(selDatum === d.datum ? "" : d.datum)}
                        className={`text-center pb-2 border-b ${selDatum === d.datum ? "border-brand-400" : "border-slate-100"}`}>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{DAY_NAMES[dowIdx(d.datum)]}</div>
                        <div className={`mx-auto my-0.5 w-7 h-7 grid place-items-center rounded-full text-sm font-bold ${d.today ? "bg-slate-900 text-white" : "text-slate-700"}`}>
                          {Number(d.datum.slice(8, 10))}
                        </div>
                        <div className={`text-[11px] font-bold tabular-nums ${d.sec ? "text-slate-700" : "text-slate-300"}`}>{d.sec ? fmtHm(d.sec) : "—"}</div>
                        {d.volno && <div className="text-[11px] font-bold text-sky-600">volno</div>}
                      </button>
                    ))}
                  </div>
                  <div className="grid" style={{ gridTemplateColumns: `48px repeat(7, minmax(0, 1fr))` }}>
                    <div className="relative" style={{ height: gridH }}>
                      {hodiny.map((h, i) => (
                        <div key={h} className="absolute right-2 -translate-y-1/2 text-[11px] font-semibold text-slate-300 tabular-nums" style={{ top: i * PX_PER_HOUR }}>
                          {String(h).padStart(2, "0")}:00
                        </div>
                      ))}
                    </div>
                    {days.map((d) => {
                      const { list, lanes } = layout(d.entries);
                      return (
                        <div key={d.datum} className={`relative border-l border-slate-100 ${d.today ? "bg-brand-50/30" : d.volno ? "bg-sky-50/40" : ""}`} style={{ height: gridH }}>
                          {hodiny.map((h, i) => <div key={h} className="absolute left-0 right-0 border-t border-slate-50" style={{ top: i * PX_PER_HOUR }} />)}
                          {list.map(({ e, s, en, lane }) => {
                            const top = ((s - bounds.fromH * 60) / 60) * PX_PER_HOUR;
                            const hgt = Math.max(18, ((en - s) / 60) * PX_PER_HOUR - 2);
                            return (
                              <button key={e.id} onClick={() => (!readOnly && (e.editable || e.zkratitelne) ? openEdit(e) : setSelDatum(d.datum))}
                                title={`${e.od}-${e.do} ${e.oblast}${e.popis ? `, ${e.popis}` : ""}`}
                                className={`absolute rounded-lg border px-1.5 py-0.5 text-left overflow-hidden transition hover:shadow-md hover:z-10 ${activityBlock(e.oblast)} ${e.invalid ? "opacity-50 line-through" : ""} ${e.editable || e.zkratitelne ? "cursor-pointer" : "cursor-default"}`}
                                style={{ top, height: hgt, left: `${(lane / lanes) * 100}%`, width: `calc(${100 / lanes}% - 4px)` }}>
                                <div className="text-[11px] font-bold leading-tight truncate">{e.oblast}</div>
                                {hgt > 30 && <div className="text-[11px] leading-tight truncate opacity-80">{e.ukol_nazev || e.popis || ""}</div>}
                                {hgt > 44 && <div className="text-[11px] leading-tight tabular-nums opacity-70">{fmtHm(e.sec)}</div>}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── MĚSÍC ── */}
          {view === "mesic" && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 sm:p-4">
              <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1">
                {DAY_NAMES.map((n) => <div key={n} className="text-center text-[11px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">{n}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
                {days[0] && Array.from({ length: dowIdx(days[0].datum) }).map((_, i) => <div key={`b${i}`} />)}
                {days.map((d) => {
                  const aktivni = selDatum === d.datum;
                  return (
                    <button key={d.datum} onClick={() => setSelDatum(aktivni ? "" : d.datum)}
                      className={`rounded-xl border p-1 sm:p-1.5 min-h-[62px] sm:min-h-[76px] text-left transition ${
                        aktivni ? "border-brand-400 ring-2 ring-brand-100 bg-white"
                        : d.future ? "border-slate-100 bg-slate-50/50"
                        : d.volno ? "border-sky-200 bg-sky-50/70"
                        : d.sec > 0 ? "border-slate-200 bg-white hover:border-brand-300"
                        : d.required ? "border-amber-200 bg-amber-50/60"
                        : "border-slate-100 bg-white"}`}>
                      <div className="flex items-center justify-between gap-1">
                        <span className={`w-5 h-5 sm:w-6 sm:h-6 grid place-items-center rounded-full text-[11px] sm:text-xs font-bold ${d.today ? "bg-slate-900 text-white" : d.future ? "text-slate-300" : "text-slate-600"}`}>
                          {Number(d.datum.slice(8, 10))}
                        </span>
                        {bulletsRequired && !d.future && (d.filled
                          ? <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                          : d.required ? <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" /> : null)}
                      </div>
                      <div className={`mt-0.5 text-[11px] sm:text-xs font-extrabold tabular-nums ${d.sec ? "text-slate-800" : "text-slate-300"}`}>
                        {d.sec ? `${Math.round((d.sec / 3600) * 10) / 10} h` : "—"}
                      </div>
                      {d.volno && <div className="text-[11px] font-bold text-sky-600 truncate">volno</div>}
                      {d.entries.length > 0 && (
                        <div className="flex gap-0.5 mt-1 h-1.5 rounded-full overflow-hidden bg-slate-100">
                          {d.entries.filter((e) => !e.invalid).slice(0, 6).map((e) => (
                            <div key={e.id} className={`${activitySolid(e.oblast)} rounded-full`} style={{ width: `${Math.max(6, (e.sec / Math.max(d.sec, 1)) * 100)}%` }} />
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
                {statActivity.slice(0, 8).map((a) => (
                  <span key={a.key} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full ${ACTIVITY_COLORS[a.key] || "bg-slate-100 text-slate-700"}`}>
                    <span className={`w-2 h-2 rounded-full ${a.color}`} /> {a.label} <span className="tabular-nums opacity-70">{fmtHm(a.sec)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {selDen && (
            // V read-only (ownerův náhled na cizí time tracking) se tužka vůbec
            // nepředává, aby nešlo otevřít úpravu ani omylem.
            <DenDetail den={selDen} useQuadrants={useQuadrants} bulletsRequired={bulletsRequired}
              onEdit={readOnly ? null : openEdit} onClose={() => setSelDatum("")} />
          )}

          {!selDen && (
            <div className="text-xs text-slate-400 text-center">
              Klikni na den a uvidíš jeho bloky i denní report.{readOnly ? "" : " Blok jde upravit do 2 hodin od konce, zkrátit do 7 dnů."}
            </div>
          )}
        </>
      )}

      <EditEntryModal
        open={!!editEntry}
        entry={editEntry}
        activities={data?.activities || []}
        useQuadrants={useQuadrants}
        canDelete={!!editEntry?.editable}
        busy={busy}
        onSave={post}
        onDelete={async () => { const id = editEntry?.id; setEditEntry(null); if (id) await post({ action: "deleteEntry", id }); }}
        onClose={() => setEditEntry(null)}
      />
    </div>
  );
}
